/**
 * AdminWhatsApp.jsx — Master Admin Only
 *
 * WhatsApp integration settings panel.
 * All settings are soft-coded — nothing sends until
 * WHATSAPP_ENABLED=true is set in backend .env.
 *
 * Features:
 *  • Enable/disable toggle (visual only — shows status)
 *  • API credentials guide
 *  • Message template editor (saved to MongoDB)
 *  • Test send button
 *  • Placeholder reference guide
 */

import { useEffect, useState } from "react";
import api from "../../services/api";

const DEFAULT_TEMPLATES = {
  appointmentConfirmed: "Hello {{name}}, your appointment at Heramba Ayurveda has been confirmed for {{date}}. Please arrive 10 minutes early. For queries call us anytime.",
  prescriptionReady:    "Hello {{name}}, your prescription from Dr. {{doctor}} is ready. Patient ID: {{patientId}}. Visit us again soon. - Heramba Ayurveda",
  followUpReminder:     "Hello {{name}}, this is a reminder for your follow-up visit at Heramba Ayurveda. Please contact us to schedule your appointment. We look forward to seeing you.",
};

const TEMPLATE_META = {
  appointmentConfirmed: {
    label: "📅 Appointment Confirmed",
    desc:  "Sent when admin sets a request status to Confirmed",
    vars:  ["{{name}}", "{{date}}"],
  },
  prescriptionReady: {
    label: "💊 Prescription Ready",
    desc:  "Sent when doctor saves a prescription",
    vars:  ["{{name}}", "{{doctor}}", "{{patientId}}"],
  },
  followUpReminder: {
    label: "🔔 Follow-up Reminder",
    desc:  "Sent manually or via scheduled follow-up",
    vars:  ["{{name}}"],
  },
};

function Badge({ enabled }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600,
      background: enabled ? "#DCFCE7" : "#FEF9C3",
      color: enabled ? "#166534" : "#92400E",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: enabled ? "#22C55E" : "#F59E0B", display: "inline-block" }} />
      {enabled ? "Active" : "Disabled (Mock Mode)"}
    </span>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E8EDF3", borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{title}</h3>
        {subtitle && <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B" }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function AdminWhatsApp() {
  const [templates,    setTemplates]    = useState({ ...DEFAULT_TEMPLATES });
  const [saved,        setSaved]        = useState({});
  const [saving,       setSaving]       = useState({});
  const [testPhone,    setTestPhone]    = useState("");
  const [testStatus,   setTestStatus]   = useState(null);
  const [testLoading,  setTestLoading]  = useState(false);
  const [enabled,      setEnabled]      = useState(false);

  /* Load templates from DB */
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await api.get("/whatsapp/templates");
        if (res.data) {
          setTemplates(t => ({ ...t, ...res.data }));
          setEnabled(res.data._enabled || false);
        }
      } catch {
        // silently use defaults if route not available yet
      }
    };
    loadTemplates();
  }, []);

  const saveTemplate = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.post("/whatsapp/templates", { key, value: templates[key] });
      setSaved(s => ({ ...s, [key]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000);
    } catch {
      alert("Failed to save template");
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const sendTest = async () => {
    if (!testPhone || testPhone.length < 10) return alert("Enter a valid 10-digit phone number");
    setTestLoading(true);
    setTestStatus(null);
    try {
      const res = await api.post("/whatsapp/test", { phone: testPhone });
      setTestStatus({ ok: true, msg: res.data?.message || "Test message queued (check backend logs if WhatsApp is disabled)" });
    } catch (err) {
      setTestStatus({ ok: false, msg: err.response?.data?.message || "Test failed" });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "28px 20px", fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0F172A" }}>💬 WhatsApp</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B" }}>Manage WhatsApp message templates and integration settings</p>
        </div>
        <Badge enabled={enabled} />
      </div>

      {/* Status banner */}
      <div style={{
        background: enabled ? "#F0FDF4" : "#FFFBEB",
        border: `1px solid ${enabled ? "#BBF7D0" : "#FDE68A"}`,
        borderRadius: 12, padding: "14px 18px", marginBottom: 24,
        display: "flex", alignItems: "flex-start", gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>{enabled ? "✅" : "⚠️"}</span>
        <div>
          {enabled ? (
            <>
              <p style={{ margin: 0, fontWeight: 600, color: "#166534", fontSize: 14 }}>WhatsApp is Active</p>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#166534" }}>Messages are being sent to patients in real time.</p>
            </>
          ) : (
            <>
              <p style={{ margin: 0, fontWeight: 600, color: "#92400E", fontSize: 14 }}>WhatsApp is in Mock Mode (Disabled)</p>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#92400E" }}>
                Messages are logged to the server console but NOT sent to patients.
                To enable, set <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4 }}>WHATSAPP_ENABLED=true</code> in your backend <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4 }}>.env</code> file.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Setup guide */}
      <Card title="🔧 How to Enable WhatsApp" subtitle="One-time setup using Meta Cloud API (free to start)">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { step: "1", text: "Go to developers.facebook.com → Create App → WhatsApp Business" },
            { step: "2", text: "Get your Phone Number ID and Permanent Access Token" },
            { step: "3", text: 'Add to backend .env file: WHATSAPP_ENABLED=true, WHATSAPP_TOKEN=your_token, WHATSAPP_PHONE_ID=your_phone_id' },
            { step: "4", text: "Redeploy backend (git push — auto deploy will handle it)" },
            { step: "5", text: "Use the Test Send below to verify it works" },
          ].map(({ step, text }) => (
            <div key={step} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#6366F1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{step}</div>
              <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.6 }}>{text}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Template editor */}
      <Card title="📝 Message Templates" subtitle="Edit the messages sent to patients. Use {{placeholders}} for dynamic values.">

        {/* Placeholder guide */}
        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px 14px", marginBottom: 20 }}>
          <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 600, color: "#475569" }}>Available placeholders:</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {["{{name}} — Patient name", "{{date}} — Appointment date", "{{doctor}} — Doctor name", "{{patientId}} — Patient ID"].map(p => (
              <code key={p} style={{ background: "#EFF6FF", color: "#3B82F6", padding: "2px 8px", borderRadius: 6, fontSize: 12 }}>{p}</code>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {Object.entries(TEMPLATE_META).map(([key, meta]) => (
            <div key={key} style={{ border: "1px solid #E8EDF3", borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{meta.label}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94A3B8" }}>{meta.desc}</p>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {meta.vars.map(v => (
                    <code key={v} style={{ background: "#F1F5F9", color: "#64748B", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{v}</code>
                  ))}
                </div>
              </div>
              <textarea
                rows={3}
                value={templates[key]}
                onChange={e => setTemplates(t => ({ ...t, [key]: e.target.value }))}
                style={{
                  width: "100%", boxSizing: "border-box",
                  border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 12px",
                  fontSize: 13, color: "#1E293B", resize: "vertical",
                  fontFamily: "inherit", lineHeight: 1.6, outline: "none",
                }}
                onFocus={e => e.target.style.borderColor = "#6366F1"}
                onBlur={e => e.target.style.borderColor = "#E2E8F0"}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  onClick={() => saveTemplate(key)}
                  disabled={saving[key]}
                  style={{
                    border: "none", cursor: "pointer", borderRadius: 8,
                    padding: "7px 18px", fontSize: 13, fontWeight: 600,
                    background: saved[key] ? "#DCFCE7" : "#6366F1",
                    color: saved[key] ? "#166534" : "#fff",
                    transition: "all 0.2s",
                  }}
                >
                  {saving[key] ? "Saving…" : saved[key] ? "✓ Saved" : "Save Template"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Test send */}
      <Card title="🧪 Test Send" subtitle="Send a test WhatsApp message to verify your setup. Works only when WhatsApp is enabled in .env">
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <input
              type="tel"
              placeholder="Enter 10-digit phone number"
              value={testPhone}
              maxLength={10}
              onChange={e => setTestPhone(e.target.value.replace(/\D/g, ""))}
              style={{
                width: "100%", boxSizing: "border-box",
                border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 12px",
                fontSize: 13, outline: "none", fontFamily: "inherit",
              }}
              onFocus={e => e.target.style.borderColor = "#6366F1"}
              onBlur={e => e.target.style.borderColor = "#E2E8F0"}
            />
          </div>
          <button
            onClick={sendTest}
            disabled={testLoading}
            style={{
              border: "none", cursor: "pointer", borderRadius: 8,
              padding: "10px 22px", fontSize: 13, fontWeight: 600,
              background: "#0F172A", color: "#fff",
              opacity: testLoading ? 0.7 : 1,
            }}
          >
            {testLoading ? "Sending…" : "Send Test"}
          </button>
        </div>
        {testStatus && (
          <div style={{
            marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
            background: testStatus.ok ? "#F0FDF4" : "#FEF2F2",
            color: testStatus.ok ? "#166534" : "#DC2626",
            border: `1px solid ${testStatus.ok ? "#BBF7D0" : "#FECACA"}`,
          }}>
            {testStatus.ok ? "✅ " : "❌ "}{testStatus.msg}
          </div>
        )}
      </Card>

    </div>
  );
}
